import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchX } from 'lucide-react';
import type { ProTraining } from '../../services/professionalAcademyApi';
import type { ProZoekTraining } from '../../services/proAcademyApi';
import { afstandKm } from '../../utils/pc4Afstand';
import { ProAcademyTegel } from './ProAcademyTegel';
import { reistijdActief, type ProFilters } from './proAcademyFilters';

const PAGINA = 24;

/**
 * Grid-overzicht zodra een filter of zoekterm actief is (spec §3). De reistijd-filter (F)
 * rekent hier 100% lokaal: eigen postcode × leslocatie-postcode via het meegeleverde PC4-bestand.
 * Volle cursussen blijven zichtbaar (VOL), net als bij de hoofdmap-chips.
 */
export const ProAcademyZoekGrid: React.FC<{
  trainingen: ProZoekTraining[];
  filters: ProFilters;
  eigenPostcode: string | null;
  afgekapt: boolean;
  titel: string;
  onOpen: (training: ProTraining) => void;
  onZaterdagUit: () => void;
  onNaarGroei: () => void;
  onWis: () => void;
}> = ({ trainingen, filters, eigenPostcode, afgekapt, titel, onOpen, onZaterdagUit, onNaarGroei, onWis }) => {
  const { t } = useTranslation('pages');
  const [zichtbaar, setZichtbaar] = useState(PAGINA);
  const metAfstand = reistijdActief(filters) && Boolean(eigenPostcode);

  useEffect(() => setZichtbaar(PAGINA), [trainingen]);

  const { lijst, verborgen } = useMemo(() => {
    const items = trainingen.map((tr) => ({ tr, km: metAfstand ? afstandKm(eigenPostcode, tr.locatie_postcode) : undefined }));
    if (!metAfstand) return { lijst: items, verborgen: 0 };
    // E-learning blijft altijd zichtbaar; live opleidingen buiten de straal (of zonder bekende locatie) vallen weg
    const binnen = items.filter((x) => x.tr.leervorm === 'e_learning' || (x.km !== null && x.km !== undefined && x.km <= (filters.km ?? Infinity)));
    return { lijst: binnen, verborgen: items.length - binnen.length };
  }, [trainingen, metAfstand, eigenPostcode, filters.km]);

  return (
    <section className="space-y-4" aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-gray-900">
          {titel}
          <span className="font-normal text-gray-400"> ({lijst.length})</span>
          {afgekapt && <span className="ml-2 text-xs font-normal text-gray-500">{t('proAcademy.afgekapt', { defaultValue: '(eerste 300 — verfijn uw filters)' })}</span>}
        </h2>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {verborgen > 0 && <span>{t('proAcademy.buitenStraal', { defaultValue: '{{n}} buiten de straal of zonder bekende leslocatie', n: verborgen })}</span>}
          <button type="button" onClick={onWis} className="text-sm font-medium text-sky-700 hover:text-sky-900">
            {t('proAcademy.wisFilters', { defaultValue: 'Alles tonen' })}
          </button>
        </div>
      </div>

      {lijst.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center">
          <SearchX className="mx-auto mb-3 h-10 w-10 text-gray-300" aria-hidden="true" />
          <h3 className="text-base font-semibold text-gray-900">{t('proAcademy.leegTitel', { defaultValue: 'Geen specifieke geaccrediteerde opleidingen gevonden voor deze combinatie.' })}</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
            {filters.dag.includes(6)
              ? t('proAcademy.leegTipZaterdag', { defaultValue: "Tip: vink de zaterdag-filter uit of bekijk de algemene 'Ontwikkeling & Groei' rij." })
              : verborgen > 0
                ? t('proAcademy.leegTipAfstand', { defaultValue: "Tip: vergroot de maximale afstand of bekijk de algemene 'Ontwikkeling & Groei' rij." })
                : t('proAcademy.leegTip', { defaultValue: "Tip: verwijder een filter of bekijk de algemene 'Ontwikkeling & Groei' rij." })}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {filters.dag.includes(6) && (
              <button type="button" onClick={onZaterdagUit} className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-400">
                {t('proAcademy.zaterdagUit', { defaultValue: 'Zaterdag-filter uitzetten' })}
              </button>
            )}
            <button type="button" onClick={onNaarGroei} className="rounded-xl bg-[#E5A425] px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-[#f0a800]">
              {t('proAcademy.naarGroei', { defaultValue: "Bekijk 'Ontwikkeling & Groei'" })}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {lijst.slice(0, zichtbaar).map(({ tr, km }) => (
              <div key={tr.id} className="relative">
                <ProAcademyTegel training={tr} onOpen={onOpen} className="w-full" />
                {typeof km === 'number' && (
                  <span className="pointer-events-none absolute right-2.5 top-2.5 z-10 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-semibold text-emerald-300 backdrop-blur">
                    📍 {t('proAcademy.afstand', { defaultValue: '{{n}} km', n: Math.round(km) })}
                  </span>
                )}
              </div>
            ))}
          </div>
          {lijst.length > zichtbaar && (
            <div className="flex justify-center">
              <button type="button" onClick={() => setZichtbaar((z) => z + PAGINA)} className="rounded-xl border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:border-gray-400">
                {t('proAcademy.meerLaden', { defaultValue: 'Meer laden ({{n}} resterend)', n: lijst.length - zichtbaar })}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default ProAcademyZoekGrid;
